import { FC, ReactNode } from "react";
import { observer } from "mobx-react";
import { Box, VBox } from "@/components-v2/Box";
import { InfoButton } from "@/components-v2/InfoButton";
import {
  getNoticeToGeneralMeetingCustomPanels,
  NoticeToGeneralMeetingCustomPanel,
  NoticeToGeneralMeetingForm,
} from "./noticeGeneralMeetingDomain";
import {
  DecisionTextsView,
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentViewingStatusLine,
  NoticeDocumentAuthStatusLine,
  YesNoQuestion,
} from "@/dbot/common/commonComponents";
import { DetailsPanel, getDecisionDetailsPanels } from "@/dbot/common/commonDomain";
import {
  ElectronicParticipationView,
  PlaceOfGeneralMeeting,
  TimeOfGeneralMeeting,
} from "@/dbot/common/commonGeneralMeetingComponents";
import { Button, LinkButton } from "@/components-v2/Button";
import { MDateInput } from "@/components-v2/DateInput";
import { Select } from "@/components-v2/Select";
import { identity } from "lodash";
import { TextInput } from "@/components-v2/TextInput";
import { AgendaActions } from "@/dbot/common/commonAgendaComponents";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { when } from "@/utils/functions";
import { Checkbox } from "@/components-v2/Checkbox";
import { useTranslation } from "@/stores/RootStoreContext";
import { DecisionType } from "@/common/decisions";
import { RadioFieldQuestion } from "@/common/commonComponents";
import {
  findShareholderPricingInterval,
  getShareIntervals,
} from "@/meetings/generalMeeting/electronicGeneralMeetingDomain";
import { LiveGeneralMeetingAnnouncementDialog } from "@/meetings/generalMeeting/LiveGeneralMeetingAnnouncementDialog";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { Alert } from "@/components-v2/Alert";
import { annualAccountWarning } from "@/dbot/common/commonGeneralMeetingDomain";
import { disabledSendNoticeInfo } from "@/dbot/summary/summaryDomain";
import {
  prepDocumentAttachmentsPanel,
  prepUploadDocumentAttachmentsButton,
} from "@/dbot/attachments";
import { TMessage } from "@/i18n/tt.ts";

const MeetingForm = observer(({ form }: { form: NoticeToGeneralMeetingForm }) => {
  return (
    <VBox>
      <RadioFieldQuestion
        field={form.meetingForm}
        fieldName={"NoticeGeneralMeetingForm"}
        question={{ key: "documents.meetingform" }}
        row
        isLocked={form.isLocked}
      />
    </VBox>
  );
});

const GeneralMeetingType: FC<{ form: NoticeToGeneralMeetingForm }> = observer(({ form }) => {
  return (
    <VBox gap={"md"}>
      <RadioFieldQuestion
        field={form.generalMeetingType}
        fieldName={"generalMeetingType"}
        question={{ key: "documents.generalMeetingType" }}
        row
        isLocked={form.isLocked}
      />
      {when(annualAccountWarning(form.context), (props) => (
        <Alert {...props} />
      ))}
    </VBox>
  );
});

const DateAndSenderOfNotice = observer(function ({ form }: { form: NoticeToGeneralMeetingForm }) {
  const { t } = useTranslation();

  return (
    <VBox>
      <MDateInput
        label={t("documents.noticeToGM.dateNoticeGeneralMeetingQuestion")}
        model={form.dateNoticeGeneralMeeting}
        readOnly={form.isLocked}
      />
      <Select
        label={t("documents.noticeToGM.questionMeetingManager")}
        value={form.noticeGeneralMeetingSender}
        items={form.allBoardMembers}
        onSelect={(item) => form.setSelectNoticeGeneralMeetingSender(item)}
        disabled={form.isLocked}
        idFn={identity}
        labelFn={identity}
      />
    </VBox>
  );
});

const DocumentsPublished = observer(function DocumentsPublished({
  form,
}: {
  form: NoticeToGeneralMeetingForm;
}) {
  const { t } = useTranslation();

  return (
    <>
      <YesNoQuestion
        question={t("documents.noticeToGM.questionOfCompanysInternetPage")}
        infoText={t("documents.noticeToGM.questionOfCompanysInternetPageHelperText")}
        field={form.companyPublishedDocumentsRegardingResolutions}
        isLocked={form.isLocked}
      />
      {form.companyPublishedDocumentsRegardingResolutions.value && (
        <Box>
          <TextInput
            id="companyPublishedDocumentsRegardingResolutionsAddress"
            label={t("documents.noticeToGM.webPageLink")}
            value={form.companyPublishedDocumentsRegardingResolutionsAddress}
            onChange={(e) =>
              form.setCompanyPublishedDocumentsRegardingResolutionsAddress(e.target.value)
            }
            readOnly={form.isLocked}
          />
        </Box>
      )}
    </>
  );
});

export const NoticeToGeneralMeetingAgendaItemDetails = observer(
  function NoticeToGeneralMeetingAgendaItemDetails({
    form,
    decisionId,
    renderDecisionForm,
    renderDecisionAttachment,
  }: {
    form: NoticeToGeneralMeetingForm;
    decisionId: string;
    renderDecisionForm: (decisionId: string) => ReactNode;
    renderDecisionAttachment: (decisionId: string) => ReactNode;
  }) {
    const decision = form.currentDecisions.find((d) => d.decisionId === decisionId);
    if (!decision) {
      return null;
    }

    const editor = form.decisionTextModels.find((d) => d.decisionId === decisionId);
    return (
      <VBox gap={"md"}>
        {renderDecisionForm(decisionId)}
        {decision.type !== DecisionType.freetext && editor && (
          <DecisionTextsView form={editor} isLocked={form.isLocked} />
        )}
        {renderDecisionAttachment(decisionId)}
      </VBox>
    );
  },
);

export const NoticeToGeneralMeetingDetails = observer(function NoticeToGeneralMeetingDetails({
  form,
}: {
  form: NoticeToGeneralMeetingForm;
}) {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <DocumentLanguageSelector field={form.documentLanguage} isLocked={form.isLocked} />
      <DateAndSenderOfNotice form={form} />
      <VBox gap={"md"}>
        <div>
          {t("documents.generalMeeting.dateQuestion")}
          &nbsp;
          <InfoButton infoText={t("documents.generalMeeting.date-guidancenote")} />
        </div>
        <TimeOfGeneralMeeting
          dateField={form.context.generalMeetingFields.dateGeneralMeeting}
          timeField={form.context.generalMeetingFields.timeGeneralMeeting}
          isLocked={form.isLocked}
        />
      </VBox>
      {form.isTooShortNotice && !form.simplifiedGeneralMeeting && (
        <YesNoQuestion
          question={t("documents.generalMeeting.shorterDeadline")}
          field={form.repairTextDeadlineGeneralMeeting}
          isLocked={form.isLocked}
        />
      )}
      <GeneralMeetingType form={form} />
      <YesNoQuestion
        question={t("documents.noticeToGM.isSimplifiedGeneralMeeting")}
        fieldName="noticeGMSimplified"
        field={form.context.generalMeetingFields.isSimplifiedGeneralMeeting}
        isLocked={form.isLocked}
      />
      <MeetingForm form={form} />
      <PlaceOfGeneralMeeting
        meetingForm={form.meetingForm}
        meetingPlace={form.context.generalMeetingFields.meetingPlace}
        inlineAddressValue={form.context.generalMeetingFields.inlineAddressValue}
        isLocked={form.isLocked}
      />
      <ConductElectronicGeneralMeeting
        conductElectronicGeneralMeeting={
          form.context.generalMeetingFields.conductElectronicGeneralMeeting.value ?? false
        }
        toggleConductElectronicGeneralMeeting={form.toggleConductElectronicGeneralMeeting}
        isConductElectricGeneralMeetingDisabled={form.context.isLocked || form.isSent}
        openGeneralMeetingReleaseDialog={() => form.openGeneralMeetingReleaseDialog}
        hasLiveGeneralMeetingFeature={form.hasLiveGeneralMeetingFeature}
        tooltip={
          form.isSent && !form.context.isLocked
            ? { key: "documents.generalMeeting.conductElectronicMeeting.activate-contact-support" }
            : undefined
        }
      />
      <ElectronicParticipationView
        meetingForm={form.meetingForm}
        electronicParticipation={form.electronicParticipation}
        electronicParticipationLink={form.electronicParticipationLink}
        isLocked={form.isLocked}
      />
      <DocumentsPublished form={form} />
      <LocalizedTextEditor model={form.additionalDetailsTextModel} />
      {form.displayElectronicGMDialog && (
        <LiveGeneralMeetingAnnouncementDialog
          closeLabel={{ key: "cancel" }}
          onClose={form.unOrderElectronicGeneralMeeting}
          onSubmit={form.orderElectronicGeneralMeeting}
          submitLabel={{ key: "release-announcement.general-meeting.order" }}
          shareholderPricingInterval={findShareholderPricingInterval(
            form.shareholderCount,
            getShareIntervals(),
          )}
        />
      )}
    </VBox>
  );
});

const ConductElectronicGeneralMeeting = observer(function ConductElectronicGeneralMeeting({
  conductElectronicGeneralMeeting,
  toggleConductElectronicGeneralMeeting,
  isConductElectricGeneralMeetingDisabled,
  openGeneralMeetingReleaseDialog,
  hasLiveGeneralMeetingFeature,
  tooltip,
}: {
  conductElectronicGeneralMeeting: boolean;
  toggleConductElectronicGeneralMeeting: () => void;
  isConductElectricGeneralMeetingDisabled: boolean;
  openGeneralMeetingReleaseDialog: () => void;
  hasLiveGeneralMeetingFeature: boolean;
  tooltip?: TMessage;
}) {
  const { t } = useTranslation();

  return (
    <VBox marginTop>
      <Checkbox
        label={
          <VBox>
            <div>{t("documents.generalMeeting.conductElectronicMeeting")}</div>
            <Box>
              {t("documents.generalMeeting.conductElectronicMeeting.description")}
              {!hasLiveGeneralMeetingFeature && (
                <LinkButton
                  label={t("release-announcement.general-meeting.schedule-more-details")}
                  onClick={openGeneralMeetingReleaseDialog}
                />
              )}
            </Box>
          </VBox>
        }
        checked={conductElectronicGeneralMeeting}
        tooltip={tooltip}
        onChange={toggleConductElectronicGeneralMeeting}
        disabled={isConductElectricGeneralMeetingDisabled}
      />
    </VBox>
  );
});

export const NoticeToGeneralMeetingDetailsPanel = observer(
  function NoticeToGeneralMeetingDetailsPanel({
    panel,
    form,
    renderDecisionForm,
    renderDecisionAttachment,
  }: {
    panel: DetailsPanel;
    form: NoticeToGeneralMeetingForm;
    renderDecisionForm: (decisionId: string) => ReactNode;
    renderDecisionAttachment: (decisionId: string) => ReactNode;
  }) {
    switch (panel.type) {
      case "decision":
        return (
          <NoticeToGeneralMeetingAgendaItemDetails
            decisionId={panel.id}
            form={form}
            renderDecisionForm={renderDecisionForm}
            renderDecisionAttachment={renderDecisionAttachment}
          />
        );
      case "custom":
        switch (panel.id) {
          case NoticeToGeneralMeetingCustomPanel.meetingDetails:
            return <NoticeToGeneralMeetingDetails form={form} />;
        }
    }
    return null;
  },
);

export const NoticeToGeneralMeetingView = observer(function NoticeToGeneralMeetingView({
  form,
}: {
  form: NoticeToGeneralMeetingForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"} paddingBottom={form.isSent ? "lg" : undefined}>
      <VBox gap="xs">
        <DocumentViewingStatusLine context={form.context} dispatch={form.dispatch} />
        <NoticeDocumentAuthStatusLine context={form.context} />
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.isLocked}
          panels={getNoticeToGeneralMeetingCustomPanels(form)}
        />
      </VBox>
      <DetailsSummaryList
        dispatch={form.dispatch}
        isLocked={form.isLocked}
        panels={getDecisionDetailsPanels(form)}
      />
      <AgendaActions
        isLocked={form.isLocked}
        context={form.context}
        dispatch={form.dispatch}
        uploadAttachments={prepUploadDocumentAttachmentsButton(form.context, form.dispatch)}
      />
      {when(prepDocumentAttachmentsPanel(form.context, form.dispatch), (props) => (
        <AttachmentsPanel {...props} />
      ))}
      {when(form.openSendNoticeDialogAction, (action) => (
        <Button variant={"primary"} label={t(action.label)} onClick={() => form.dispatch(action)} />
      ))}
      {when(disabledSendNoticeInfo(form.context), (msg) => (
        <Alert type="info" message={msg} />
      ))}
    </VBox>
  );
});
